import { Injectable, inject } from '@angular/core';
import { AppointmentActivity } from '../../classes/activity/appointment.activity.class';
import { AuthenticationService } from '../../services/authentication.service';
import { GPS_STATUS, LocationOfflineService } from '../../services/location/location.service';
import { GeolocationService } from '../../services/location/gelolocation';
import { calculateDistance } from '../../services/location/utils';
import { MeetingDataService } from './meeting.data.service';
import { ActivityService } from '../../services/activity/activity.service';
import { DateTimeFormatsService } from '../../services/date-time-formats/date-time-formats.service';
import { DeviceService } from '../../services/device/device.service';
import { TranslateService } from '@ngx-translate/core';
import { FeatureActionsMap } from '../../classes/authentication/user.class';
import { ReasonForLocationCaptureFailure } from '../../enums/presentation/gps-data.enum';
import { DistanceUnits } from '../../classes/my-assistant/my-assistant.class';
import { firstValueFrom } from 'rxjs';

/**
 * Service responsible for capturing GPS location and distance from contact's location
 * Used when creating or modifying meetings to track location-based data
 */
@Injectable({
  providedIn: 'root'
})
export class MeetingLocationCaptureService {
  private readonly authService = inject(AuthenticationService);
  private readonly locationService = inject(LocationOfflineService);
  private readonly geolocationService = inject(GeolocationService);
  private readonly meetingDataService = inject(MeetingDataService);
  private readonly activityService = inject(ActivityService);
  private readonly dateTimeFormatsService = inject(DateTimeFormatsService);
  private readonly device = inject(DeviceService);
  private readonly translate = inject(TranslateService);

  /**
   * Capture user's GPS location and calculate distance from contact's location
   * Triggered when meeting location changes or contacts are added
   * This is where the location permission popup is triggered
   */
  public async captureContactLocationDistance(meeting: AppointmentActivity): Promise<void> {
    try {
      // Check if feature is enabled
      if (!this.authService.hasFeatureAction(FeatureActionsMap.CAPTURE_GPS_DISTANCE_FROM_CONTACT)) {
        return;
      }

      // Initialize gpsCheckInDetails if it doesn't exist
      if (!meeting.gpsCheckInDetails) {
        meeting.gpsCheckInDetails = {};
      }

      // Check if contact exists - required for GPS capture (fixes OMNI-56937 & TC-147264)
      // If no contact, clear GPS and return (location may be set from account but GPS requires contact)
      const contact = meeting.contacts?.[0];
      if (!contact) {
        console.log('captureContactLocationDistance: No contact found in meeting, clearing GPS check-in details');
        await this.#clearGPSCheckInDetails(meeting);
        return;
      }

      // Check if location is empty or invalid
      if (!meeting.location || 
          (typeof meeting.location === 'string' && meeting.location.trim() === '') || 
          meeting.location === 'No Location' || 
          meeting.location === this.translate.instant('NO_LOCATION')) {
        console.log('captureContactLocationDistance: Location is empty or invalid, clearing GPS check-in details');
        await this.#clearGPSCheckInDetails(meeting);
        return;
      }

      // Check if offline
      if (this.device.isOffline) {
        console.log('captureContactLocationDistance: Device is offline');
        meeting.indskr_reasonforlocationcapturefailure = ReasonForLocationCaptureFailure['Low Network/Offline'];
        await this.#updateMeetingLocationFailure(meeting);
        return;
      }

      let coordinates = {
        latitude: meeting.indskr_meetinglocationlatitude,
        longitude: meeting.indskr_meetinglocationlongitude
      };
      // Deprecated: geocoding to generate coordinates from address string is no longer performed
      // when the meeting location lacks latitude/longitude (CAPTURE_GPS_DISTANCE_FROM_CONTACT flow)
      // if (!coordinates.latitude || !coordinates.longitude) {
      //   const coordinatesFromAddress = await convertAddressToCoords(meeting.location);
      //   if (coordinatesFromAddress) {
      //     coordinates.latitude = coordinatesFromAddress.latitude;
      //     coordinates.longitude = coordinatesFromAddress.longitude;
      //   }
      // }

      // Check if location service is enabled
      if (!await firstValueFrom(this.geolocationService.isEnabled$) && (!coordinates.latitude || !coordinates.longitude)) {
        console.log('captureContactLocationDistance: Location service disabled');
        meeting.indskr_reasonforlocationcapturefailure = ReasonForLocationCaptureFailure['Location Service Disabled'];
        await this.#updateMeetingLocationFailure(meeting);
        return;
      }

      if (!coordinates.latitude || !coordinates.longitude) {
        // Deprecated: no notification shown when coordinates are missing (geocoding removed)
        // console.log('captureContactLocationDistance: Could not get coordinates, clearing GPS check-in details');
         await this.#clearGPSCheckInDetails(meeting);
         meeting.indskr_reasonforlocationcapturefailure = ReasonForLocationCaptureFailure['Unable to find Geocodes'];
         await this.#updateMeetingLocationFailure(meeting);
        // this.notificationService.notify(this.translate.instant('INVALID_ADDRESS'), 'GPS Capture', 'top', ToastStyle.DANGER, 3000);
        return;
      }

      // Get user's current location
      // Since geolocation is initialized at app launch, no permission prompt occurs here
      const userLocation = await this.geolocationService.getCurrentPosition();
      
      if (!userLocation || !userLocation.latitude || !userLocation.longitude) {
        console.error('captureContactLocationDistance: Could not get user location');
        meeting.indskr_reasonforlocationcapturefailure = ReasonForLocationCaptureFailure['Unable to Identify Reason'];
        await this.#updateMeetingLocationFailure(meeting);
        return;
      }

      // Calculate distance - use regional settings distance unit
      const regionalDistanceUnit = this.dateTimeFormatsService.distanceUnitSelected.value;
      const distanceUnit = regionalDistanceUnit === 'km' ? 'K' : undefined;
      const distance = calculateDistance(
        { latitude: coordinates.latitude, longitude: coordinates.longitude },
        { latitude: userLocation.latitude, longitude: userLocation.longitude },
        distanceUnit
      );

      // Prepare payload
      const payload = {
        indskr_checkinlatitude: coordinates.latitude,
        indskr_checkinlongitude: coordinates.longitude,
        indskr_checkoutlatitude: userLocation.latitude,
        indskr_checkoutlongitude: userLocation.longitude,
        indskr_checkoutdistance: 0,
        indskr_checkindistance: distance,
        indskr_checkindatetime: new Date(),
        indskr_checkinstatus: GPS_STATUS.VALID,
        indskr_checkoutstatus: GPS_STATUS.VALID,
        indskr_checkoutdatetime: new Date(),
        indskr_checkindistanceunitnew: regionalDistanceUnit === 'km' ? DistanceUnits.KM : DistanceUnits.Miles,
        indskr_checkoutdistanceunit: regionalDistanceUnit === 'km' ? DistanceUnits.KM : DistanceUnits.Miles,
      };
      // Only use existing GPS record ID if this is NOT a cloned meeting (OMNI-57412)
      // Cloned meetings should always CREATE a new GPS check-in details record
      if (meeting.gpsCheckInDetails?.indskr_gpscheckindetailsid && !meeting.isClone) {
        payload['indskr_gpscheckindetailsid'] = meeting.gpsCheckInDetails.indskr_gpscheckindetailsid;
      }

      // Update GPS Check-in Details
      const response = await this.meetingDataService.updateMeetingCheckInCheckout(meeting, payload);
      if (response) {
        // Update meeting object following existing pattern
        meeting.gpsCheckInDetails.indskr_checkindatetime = response.indskr_checkindatetime || payload.indskr_checkindatetime;
        meeting.gpsCheckInDetails.indskr_checkinlatitude = response.indskr_checkinlatitude || payload.indskr_checkinlatitude;
        meeting.gpsCheckInDetails.indskr_checkinlongitude = response.indskr_checkinlongitude || payload.indskr_checkinlongitude;
        meeting.gpsCheckInDetails.indskr_checkoutlatitude = response.indskr_checkoutlatitude || payload.indskr_checkoutlatitude;
        meeting.gpsCheckInDetails.indskr_checkoutlongitude = response.indskr_checkoutlongitude || payload.indskr_checkoutlongitude;
        meeting.gpsCheckInDetails.indskr_checkinstatus = response.indskr_checkinstatus || payload.indskr_checkinstatus;
        meeting.gpsCheckInDetails.indskr_checkindistance = distance;
        meeting.gpsCheckInDetails.indskr_gpscheckindetailsid = response.indskr_gpscheckindetailsid;
        
        // Update meeting location coordinates
        meeting.indskr_meetinglocationlatitude = coordinates.latitude;
        meeting.indskr_meetinglocationlongitude = coordinates.longitude;
        meeting.indskr_meetingdistance = distance;
        
        // Clear any failure reason
        meeting.indskr_reasonforlocationcapturefailure = undefined;

        // Reset isClone flag after successful GPS capture (OMNI-57412)
        // Now the meeting has its own GPS record ID
        meeting.isClone = false;

        // Update offline data if needed
        if (this.device.isOffline || this.activityService.hasOfflineMeetingData(meeting.ID)) {
          await this.activityService.upsertMeetingsOfflineData(meeting);
        }

        console.log('captureContactLocationDistance: Successfully captured GPS location and distance');
      }
    } catch (error) {
      console.error('captureContactLocationDistance: Error capturing location', error);
      meeting.indskr_reasonforlocationcapturefailure = ReasonForLocationCaptureFailure['Unable to Identify Reason'];
      await this.#updateMeetingLocationFailure(meeting);
    }
  }

  /**
   * Clear GPS check-in details when location is empty or invalid
   */
  async #clearGPSCheckInDetails(meeting: AppointmentActivity): Promise<void> {
    try {
      // Initialize gpsCheckInDetails if it doesn't exist
      if (!meeting.gpsCheckInDetails) {
        meeting.gpsCheckInDetails = {};
      }

      // Clear all GPS fields in gpsCheckInDetails
      meeting.gpsCheckInDetails.indskr_checkinlatitude = null;
      meeting.gpsCheckInDetails.indskr_checkinlongitude = null;
      meeting.gpsCheckInDetails.indskr_checkoutlatitude = null;
      meeting.gpsCheckInDetails.indskr_checkoutlongitude = null;
      meeting.gpsCheckInDetails.indskr_checkindistance = null;
      meeting.gpsCheckInDetails.indskr_checkindatetime = null;
      meeting.gpsCheckInDetails.indskr_checkinstatus = null;
      meeting.gpsCheckInDetails.indskr_checkindistanceunitnew = null;
      meeting.gpsCheckInDetails.indskr_checkoutdistanceunit = null;

      // Clear meeting location coordinates
      meeting.indskr_meetinglocationlatitude = null;
      meeting.indskr_meetinglocationlongitude = null;
      meeting.indskr_meetingdistance = undefined;

      // Clear failure reason
      meeting.indskr_reasonforlocationcapturefailure = undefined;

      // If GPS record exists, update it via API (or update offline data)
      if (meeting.gpsCheckInDetails?.indskr_gpscheckindetailsid) {
        if (!this.device.isOffline) {
          try {
            const payload = {
              indskr_checkinlatitude: null,
              indskr_checkinlongitude: null,
              indskr_checkoutlatitude: null,
              indskr_checkoutlongitude: null,
              indskr_checkindistance: null,
              indskr_checkindatetime: null,
              indskr_checkinstatus: null,
              indskr_checkindistanceunitnew: null,
              indskr_checkoutdistanceunit: null,
              indskr_gpscheckindetailsid: meeting.gpsCheckInDetails.indskr_gpscheckindetailsid
            };
            await this.meetingDataService.updateMeetingCheckInCheckout(meeting, payload);
          } catch (error) {
            console.error('clearGPSCheckInDetails: Error clearing GPS data via API', error);
          }
        }
      }

      // Update offline data
      if (this.device.isOffline || this.activityService.hasOfflineMeetingData(meeting.ID)) {
        await this.activityService.upsertMeetingsOfflineData(meeting);
      }

      console.log('clearGPSCheckInDetails: Successfully cleared GPS check-in details');
    } catch (error) {
      console.error('clearGPSCheckInDetails: Error clearing GPS check-in details', error);
    }
  }

  /**
   * Update meeting with location capture failure reason
   */
  async #updateMeetingLocationFailure(meeting: AppointmentActivity): Promise<void> {
    try {
      // Update offline data
      if (this.device.isOffline || this.activityService.hasOfflineMeetingData(meeting.ID)) {
        await this.activityService.upsertMeetingsOfflineData(meeting);
      }
    } catch (error) {
      console.error('updateMeetingLocationFailure: Error updating meeting', error);
    }
  }
}

